<template>
  <div>
    <v-row>
      <v-col
        cols="12"
        md="12"
      >
        <v-tabs
          v-model="tab"
          @change="changeRoute"
        >
          <v-tab>
            General
          </v-tab>
          <v-tab>
            Manage Schedule
          </v-tab>
          <v-tab>
            <v-icon left>
              mdi-watermark
            </v-icon>
            Bug
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab">
          <v-tab-item>
            <v-card :loading="loading">
              <div class="row p-t-20">
                <div class="col-md-3">
                  <v-img
                    width="200"
                    height="200"
                    contain
                    class="grey darken-4 m-b-20 m-l-30"
                    :src="channel.logo ? channel.logo : defaultIcon"
                  />
                  <v-file-input
                    v-model="iconFile"
                    class="pointer"
                    placeholder="Upload a new logo (JPEG, PNG)"
                    truncate-length="15"
                    label="File input"
                    style="width: 230px;"
                    show-size
                    accept="image/png, image/jpeg"
                    prepend-icon="mdi-paperclip"
                    @change="fileUpload('logo', iconFile)"
                  />
                </div>
                <div class="col-md-8">
                  <v-text-field
                    v-model="channel.name"
                    label="Channel name*"
                    :counter="255"
                    clearable
                    :rules="rules.nameRules"
                    required
                  />
                  <v-textarea
                    v-model="channel.description"
                    append-icon="mdi-comment-text"
                    label="Channel description"
                    rows="2"
                    auto-grow
                    counter
                  />
                  <v-combobox
                    v-model="channel.timezone"
                    append-icon="zmdi-time-interval"
                    item-text="name"
                    :items="timezones"
                    label="Channel timezone"
                    return-object
                  />
                </div>
              </div>
              <div class="row p-l-30 p-r-30">
                <div class="col-md-12">
                  <v-item-group mandatory>
                    <p class="text-h5 m-b-0">
                      <i class="fas fa-film p-r-10" />Layout & Templates
                    </p>
                    <v-card
                      flat
                      class="p-b-10"
                    >
                      <v-card-text class="p-l-0 p-b-10 p-r-0">
                        <div
                          style="background-color: #E3F2FD"
                          class="p-l-20 p-r-20"
                        >
                          <p class="text-h5 primary--text p-t-10">
                            <i class="fas fa-layer-group p-r-10" />{{ channel.layout.name }}
                          </p>
                          <img
                            v-if="channel.layout.pictureURL"
                            :src="channel.layout.pictureURL"
                          >
                          <div class="m-t-10 text-body-1 p-b-10">
                            {{ channel.layout.description }}
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                    <v-row>
                      <v-col
                        v-for="template in channel.templates"
                        :key="template.id"
                        cols="12"
                        md="4"
                      >
                        <v-item>
                          <v-card
                            class="d-flex align-center"
                            dark
                            max-width="400"
                          >
                            <v-scroll-y-transition>
                              <div
                                class="flex-grow-1 text-center"
                              >
                                <v-card>
                                  <v-img
                                    max-width="400"
                                    :src="template.template.pictureURL"
                                  >
                                    <div
                                      class="v-card--reveal"
                                      style="height: 100%;"
                                    >
                                      <v-btn
                                        color="primary"
                                        dark
                                        @click="playStreamByURL(template.template.streamURL,
                                                                template.template.type + ': ' + template.template.name,
                                                                template.template.description)"
                                      >
                                        <i class="fas fa-play p-r-5" />
                                        {{ template.template.type }}: {{ template.template.name }}
                                      </v-btn>
                                    </div>
                                  </v-img>
                                </v-card>
                              </div>
                            </v-scroll-y-transition>
                          </v-card>
                        </v-item>
                      </v-col>
                    </v-row>
                  </v-item-group>
                </div>
              </div>

              <v-card-actions class="m-r-20 p-b-20">
                <v-spacer />
                <v-btn
                  large
                  :loading="isSaving"
                  :disabled="!isFormValid || isSaving"
                  color="success"
                  @click="save()"
                >
                  Save
                  <v-icon right>
                    mdi-content-save
                  </v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-tab-item>
          <v-tab-item>
            <v-card
              class="p-b-20 p-l-30 p-r-30"
              :loading="loading"
            >
              <div class="row">
                <div class="col-md-12 p-b-0">
                  <v-switch
                    v-model="autoMode"
                    color="success"
                    inset
                    @click="changeMode()"
                  >
                    <template v-slot:label>
                      <v-list-item v-ripple>
                        <v-list-item-content v-if="autoMode">
                          <v-list-item-title>Auto Scheduler</v-list-item-title>
                          <v-list-item-subtitle>
                            Enable to publish programs randomly
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-content v-else>
                          <v-list-item-title>Manual Scheduler</v-list-item-title>
                          <v-list-item-subtitle>
                            Get absolute control over your schedule time
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-switch>
                  <v-alert
                    v-if="showNote"
                    type="info"
                    outlined
                    dismissible
                    text
                  >
                    Note: when you switch the mode, all scheduled programs
                    starting from today will be removed.
                  </v-alert>
                </div>
                <div
                  v-if="autoMode"
                  class="col-md-12 p-t-0"
                >
                  <v-tabs vertical>
                    <!--v-tab class="justify-start">
                      <v-icon left>
                        mdi-playlist-play
                      </v-icon>
                      Sources ({{ selectedTags.length }})
                    </v-tab>
                    <v-tab class="justify-start">
                      <v-icon left>
                        mdi-chart-gantt
                      </v-icon>
                      Rules ({{ scheduleRules.length }})
                    </v-tab>
                    <v-tab class="justify-start">
                      <v-icon left>
                        mdi-pin
                      </v-icon>
                      Fixed VOD
                    </v-tab-->
                    <v-tab class="justify-start">
                      <v-icon left>
                        mdi-grid
                      </v-icon>
                      Grid
                    </v-tab>
                    <!-- Sources -->
                    <!--v-tab-item>
                      <v-card
                        flat
                        class="p-l-20"
                      >
                        <div class="col-md-12 p-b-5">
                          Select at least one media source:
                        </div>
                        <div
                          v-if="tags.length === 0 && !loading"
                          class="col-md-12"
                        >
                          <v-alert
                            type="warning"
                            outlined
                            text
                          >
                            Looks like you don't have any media sources.
                            <v-btn
                              :disabled="loading"
                              color="primary"
                              @click="goToNewImport()"
                            >
                              <i class="zmdi zmdi-plus p-r-5" />
                              add source
                            </v-btn>
                          </v-alert>
                        </div>
                        <div
                          v-if="tags.length"
                          class="col-md-12"
                        >
                          <v-list-item-group
                            v-model="selectedTags"
                            active-class=""
                            multiple
                            @change="changeSelectedTags()"
                          >
                            <v-list-item
                              v-for="tag in tags"
                              :key="tag.id"
                              :ripple="false"
                            >
                              <template v-slot:default="{ active }">
                                <v-list-item-action>
                                  <v-checkbox
                                    :input-value="active"
                                  />
                                </v-list-item-action>
                                <v-list-item-content>
                                  <v-list-item-title class="text-body-1">
                                    {{ tag.name }}
                                  </v-list-item-title>
                                </v-list-item-content>
                                <v-list-item-action @click.stop="changeProportion()">
                                  <v-text-field
                                    v-model="tag.proportion"
                                    hide-details
                                    type="number"
                                    style="max-width: 120px;"
                                    :disabled="!active"
                                    suffix="%"
                                    :rules="rules.proportion"
                                    label="Proportion"
                                    @click.stop="changeProportion()"
                                  />
                                </v-list-item-action>
                              </template>
                            </v-list-item>
                          </v-list-item-group>
                        </div>
                      </v-card>
                    </v-tab-item-->
                    <!-- Rules -->
                    <!--v-tab-item>
                      <v-card
                        flat
                        class="p-l-20"
                      >
                        <div class="col-md-12">
                          Set the auto-scheduling rules based on the time of content arrival:
                          <table class="table table-bordered import-table m-t-10">
                            <thead>
                              <tr>
                                <th scope="col" />
                                <th scope="col">
                                  Content interval
                                </th>
                                <th scope="col">
                                  Priority*
                                </th>
                                <th scope="col">
                                  Min repetition interval*
                                </th>
                                <th scope="col" />
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="(rule, i) in scheduleRules"
                                :key="'rule'+i"
                              >
                                <td style="width: 50px;">
                                  #{{ i+1 }}
                                </td>
                                <td>
                                  <span class="text-overline p-l-5">
                                    from
                                    <span v-if="rule.range[0] === 0">now</span>
                                    <span v-else-if="rule.range[0] === max">max</span>
                                    <span v-else>-{{ rule.range[0] }}</span>
                                    to
                                    <span v-if="rule.range[1] === max">max ({{ (max/24).toFixed(0) }} days)</span>
                                    <span v-else>-{{ rule.range[1] }} hours</span>
                                  </span>
                                  <span
                                    v-if="!rule.valid && rule.priority"
                                    class="text-overline p-l-20 error--text"
                                  >
                                    Invalid rule
                                  </span>
                                  <v-range-slider
                                    v-model="rule.range"
                                    step="1"
                                    ticks
                                    :max="max"
                                    :min="min"
                                    @change="changeRule(i, rule)"
                                  />
                                </td>
                                <td style="width: 150px;">
                                  <v-select
                                    v-model="rule.priority"
                                    :items="rule.priorities"
                                    @change="changePriority()"
                                  />
                                </td>
                                <td style="width: 150px;">
                                  <v-text-field
                                    v-model="rule.distance"
                                    type="number"
                                    :suffix="rule.distance === 1 ? 'program' : 'programs'"
                                    :rules="rules.adInsertionInterval"
                                    @change="validateRule(rule)"
                                  />
                                </td>
                                <td style="width: 80px;">
                                  <v-btn
                                    class="ma-2"
                                    text
                                    :disabled="i === 0 || i !== scheduleRules.length - 1"
                                    :color="i === scheduleRules.length - 1 ? 'red lighten-2' : ''"
                                    icon
                                    @click="deleteRule(i)"
                                  >
                                    <v-icon>mdi-trash-can-outline</v-icon>
                                  </v-btn>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                          <v-btn
                            class="m-t-10"
                            color="primary"
                            @click="addRule()"
                          >
                            <i class="zmdi zmdi-plus p-r-5" />
                            add rule
                          </v-btn>
                        </div>
                      </v-card>
                    </v-tab-item-->
                    <!-- Fixed VOD -->
                    <!--v-tab-item>
                      <v-card
                        flat
                        class="p-l-20"
                      >
                        <v-row>
                          <v-col
                            md="4"
                            class="h5 vcenter"
                            style="color: #304156;"
                          >
                            Fixed VOD (3)
                          </v-col>
                          <v-col
                            md="8"
                            class="text-right"
                          >
                            <v-btn
                              color="primary"
                              @click="fixedVODDialog = true"
                            >
                              <i class="zmdi zmdi-plus p-r-5" />
                              add vod
                            </v-btn>
                          </v-col>
                        </v-row>
                        <table class="table table-hover table-fixed-vod m-b-20">
                          <thead>
                            <tr>
                              <th
                                scope="col"
                                style="width: 70px;"
                              >
                                #
                              </th>
                              <th scope="col">
                                Item
                              </th>
                              <th scope="col" />
                              <th
                                scope="col"
                                style="width: 100px;"
                              >
                                Day
                              </th>
                              <th
                                scope="col"
                                style="width: 100px;"
                              >
                                Time
                              </th>
                              <th
                                scope="col"
                                style="width: 120px;"
                              />
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <th scope="row">
                                1
                              </th>
                              <td colspan="2">
                                <v-avatar
                                  tile
                                  class="m-r-10"
                                  color="grey"
                                />
                                Movie1
                              </td>
                              <td class="text-body-1">
                                Monday
                              </td>
                              <td>
                                <v-chip>
                                  11:00
                                </v-chip>
                              </td>
                              <td>
                                <v-btn
                                  text
                                  color="success"
                                  icon
                                >
                                  <v-icon>mdi-pencil-box-outline</v-icon>
                                </v-btn>
                                <v-btn
                                  text
                                  color="red lighten-2"
                                  icon
                                >
                                  <v-icon>mdi-trash-can-outline</v-icon>
                                </v-btn>
                              </td>
                            </tr>
                            <tr class="stub">
                              <th scope="row">
                                2
                              </th>
                              <td>
                                <v-avatar
                                  tile
                                  class="m-r-10"
                                  color="grey"
                                />
                                Slot1 (60 min reserved)
                              </td>
                              <td>
                                <span class="grey--text text--darken-2">
                                  Not bound
                                </span>
                                <v-btn
                                  text
                                  color="primary"
                                  icon
                                >
                                  <v-icon>mdi-link-variant</v-icon>
                                </v-btn>
                              </td>
                              <td class="text-body-1">
                                Thursday
                              </td>
                              <td>
                                <v-chip>
                                  11:00
                                </v-chip>
                              </td>
                              <td>
                                <v-btn
                                  text
                                  color="success"
                                  icon
                                >
                                  <v-icon>mdi-pencil-box-outline</v-icon>
                                </v-btn>
                                <v-btn
                                  text
                                  color="red lighten-2"
                                  icon
                                >
                                  <v-icon>mdi-trash-can-outline</v-icon>
                                </v-btn>
                              </td>
                            </tr>
                            <tr>
                              <th scope="row">
                                3
                              </th>
                              <td colspan="2">
                                <v-avatar
                                  tile
                                  class="m-r-10"
                                  color="grey"
                                />
                                Movie2
                              </td>
                              <td class="text-body-1">
                                Thursday
                              </td>
                              <td>
                                <v-chip>
                                  11:00
                                </v-chip>
                              </td>
                              <td>
                                <v-btn
                                  text
                                  color="success"
                                  icon
                                >
                                  <v-icon>mdi-pencil-box-outline</v-icon>
                                </v-btn>
                                <v-btn
                                  text
                                  color="red lighten-2"
                                  icon
                                >
                                  <v-icon>mdi-trash-can-outline</v-icon>
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </v-card>
                    </v-tab-item-->
                    <!-- Grid -->
                    <v-tab-item>
                      <v-card
                        flat
                        class="p-l-20"
                      >
                        <div class="col-md-12">
                          Forward scheduling depth:
                          <v-text-field
                            v-model="channel.autoSchedulerDepth"
                            style="width: 150px;"
                            type="number"
                            suffix="minutes"
                          />
                        </div>
                      </v-card>
                    </v-tab-item>
                  </v-tabs>
                </div>
              </div>
              <v-card-actions class="m-r-0">
                <v-spacer />
                <v-btn
                  large
                  :loading="isSaving"
                  :disabled="!isScheduleSettingsValid || isSaving"
                  color="success"
                  @click="saveScheduleSettings()"
                >
                  <span v-if="autoMode">
                    <span v-if="!channel.autoScheduler">Start</span>
                    <span v-else>Update</span>
                    Auto-Scheduler</span>
                  <span v-else>Save</span>
                  <v-icon right>
                    mdi-content-save
                  </v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-tab-item>
          <!-- Bug -->
          <v-tab-item>
            <v-card
              class="p-b-20 p-l-30 p-r-30"
              :loading="loading"
            >
              <v-row class="p-b-0 p-t-0">
                <v-col
                  cols="12"
                  sm="12"
                  md="12"
                  class="p-b-0 p-t-0"
                >
                  <v-switch
                    v-model="channel.watermark.enabled"
                    color="success"
                    inset
                  >
                    <template v-slot:label>
                      <v-list-item v-ripple>
                        <v-list-item-content>
                          <v-list-item-title>Enable channel bug</v-list-item-title>
                          <v-list-item-subtitle>
                            A bug's main objective is to brand your video program with an identity.
                            This helps viewers to quickly identify what network is running a
                            particular program.
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-switch>
                </v-col>
              </v-row>
              <v-row
                v-if="channel.watermark.enabled"
                no-gutters
              >
                <v-col class="p-t-0">
                  <v-img
                    width="150"
                    height="150"
                    contain
                    class="grey darken-4 m-b-10"
                    :src="channel.watermarkPicture ? channel.watermarkPicture : defaultIcon"
                  />
                  <v-file-input
                    v-model="watermarkFile"
                    placeholder="Pick a bug* (PNG)"
                    style="width: 145px;"
                    class="pointer"
                    truncate-length="30"
                    show-size
                    accept="image/png"
                    prepend-icon="mdi-camera"
                    @change="fileUpload('watermarkPicture', watermarkFile)"
                  />
                  <v-col
                    cols="12"
                    sm="6"
                    md="12"
                    class="p-t-40"
                  >
                    <v-switch
                      v-model="watermarkPreviewCycle"
                      label="Cycle Slides"
                      inset
                    />
                  </v-col>
                </v-col>
                <v-col class="p-t-0 justify-center">
                  <v-carousel
                    :cycle="watermarkPreviewCycle"
                    style="width: 640px !important; height: 360px !important;"
                    hide-delimiter-background
                    show-arrows-on-hover
                  >
                    <v-carousel-item
                      v-for="(backgroundURI, i) in watermarkBackgrounds"
                      :key="i"
                      style="width: 640px !important; height: 360px !important;"
                    >
                      <v-row
                        class="fill-height"
                        align="center"
                        justify="center"
                      >
                        <v-img
                          style="width: 640px !important; height: 360px !important;"
                          :src="backgroundURI"
                        >
                          <v-card-title
                            class="white--text"
                            :class="`watermark-position-${channel.watermark.position}`"
                          >
                            <v-avatar
                              v-if="!channel.watermarkPicture"
                              :class="`avatar-size-${channel.watermark.size}`"
                              style="margin: 10px 18px;"
                              color="primary"
                            />
                            <img
                              v-if="channel.watermarkPicture"
                              :class="[`watermark-size-${channel.watermark.size}`]"
                              style="margin: 10px 20px;"
                              :src="channel.watermarkPicture"
                              alt=""
                            >
                          </v-card-title>
                        </v-img>
                      </v-row>
                    </v-carousel-item>
                  </v-carousel>
                </v-col>
                <v-col class="p-t-0">
                  <h4 class="h6 m-l-50">
                    Position:
                  </h4>
                  <v-radio-group
                    v-model="channel.watermark.position"
                    class="radios m-t-5 p-l-50"
                  >
                    <v-radio
                      label="Top left"
                      value="topLeft"
                    />
                    <v-radio
                      label="Top center"
                      value="topCenter"
                    />
                    <v-radio
                      label="Top right"
                      value="topRight"
                    />
                    <v-radio
                      label="Bottom left"
                      value="bottomLeft"
                    />
                    <v-radio
                      label="Bottom center"
                      value="bottomCenter"
                    />
                    <v-radio
                      label="Bottom right"
                      value="bottomRight"
                    />
                  </v-radio-group>
                  <h4 class="h6 m-l-50">
                    Size of longest side:
                  </h4>
                  <v-radio-group
                    v-model="channel.watermark.size"
                    class="radios m-t-5 p-l-50"
                  >
                    <v-radio
                      label="200 pixels"
                      value="200"
                    />
                    <v-radio
                      label="150 pixels"
                      value="150"
                    />
                    <v-radio
                      label="100 pixels"
                      value="100"
                    />
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-row v-if="channel.watermark.enabled">
                <v-col
                  cols="12"
                  sm="6"
                  md="6"
                  class="text-body-2"
                >
                  Bugs typically are semi-transparent and small in size.
                  For HD content the bug size shall be around 200×200 pixels and shall be
                  uploaded in PNG format. Bugs can be animated and can flip, rotate, spin or
                  otherwise have any moving properties. In this case you have to upload an APNG
                  file.
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                  md="6"
                  class="text-body-2"
                >
                  Choose subtle coloring, unless, of course, the color is central to the brand.
                  A good bug should still be easily identified. Consider adding an outer glow
                  or drop shadow to the graphic to help it slightly stand out from the video
                  program. If you choose to add either of these styles you'll need to make
                  sure you preserve the alpha channel.
                </v-col>
              </v-row>
              <v-card-actions class="p-b-20">
                <v-spacer />
                <v-btn
                  large
                  :loading="isSaving"
                  :disabled="!isFormValid || isSaving"
                  color="success"
                  @click="save()"
                >
                  Save
                  <v-icon right>
                    mdi-content-save
                  </v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>
    <v-bottom-sheet
      v-model="openPlayer"
      max-width="960"
      :persistent="false"
    >
      <v-card tile>
        <iframe
          id="serviceFrameSend"
          :src="frameSrc"
          width="100%"
          height="380"
          frameborder="0"
        />
        <v-list>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>{{ player.assetName }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ player.assetDescription }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
        <v-card-actions>
          <v-spacer />
          <v-btn
            depressed
            @click="closeStream()"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-bottom-sheet>

    <!-- Fixed VOD Dialog -->
    <v-dialog
      v-model="fixedVODDialog"
      max-width="600"
      persistent
    >
      <v-card class="pa-3">
        <v-card-title class="text-h5">
          New Fixed VOD
        </v-card-title>
        <v-card-text>
          <div class="row">
            <div class="col-4">
              <v-radio-group
                v-model="fixedVOD.type"
                class="radios"
              >
                <v-radio
                  label="Asset"
                  value="asset"
                />
                <v-radio
                  label="Empty slot"
                  value="slot"
                />
              </v-radio-group>
            </div>
            <div
              v-if="fixedVOD.type === 'asset'"
              class="col-8"
            >
              <v-select
                v-model="fixedVOD.assetId"
                label="Asset"
                item-text="title"
                item-value="id"
                :items="fixedVODAssets"
              />
            </div>
            <div
              v-if="fixedVOD.type === 'slot'"
              class="col-8"
            >
              <v-text-field
                v-model="fixedVOD.slot.name"
                label="Slot name*"
                :counter="255"
                clearable
                :rules="rules.nameRules"
                required
              />
              <v-text-field
                v-model="fixedVOD.slot.duration"
                label="Duration in minutes"
              />
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <v-select
                v-model="fixedVOD.day"
                label="Day"
                :items="fixedVODDays"
              />
            </div>
            <div class="col-md-6">
              <v-text-field
                v-model="fixedVOD.time"
                hide-details
                label="Time"
                type="time"
              />
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            depressed
            text
            @click="fixedVODDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            depressed
            color="primary"
            @click="addFixedVOD()"
          >
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import Vue from 'vue'
  import {
    channelToJsonAPI, processChannel, processTimezone, processTag,
  } from '@/store/converters'
  import { LAYOUT_TYPE } from '@/shared/types'

  export default {
    metaInfo: {
      title: 'Edit channel',
    },
    data () {
      return {
        tab: 0,
        channel: {
          name: '',
          layout: {},
          templates: [],
          watermark: {},
        },
        iconFile: null,
        watermarkSaved: {},
        watermarkFile: null,
        watermarkPreviewCycle: true,
        watermarkBackgrounds: [
          '/images/bug_background/cartoon.jpg',
          '/images/bug_background/crime.jpg',
          '/images/bug_background/movie.jpg',
          '/images/bug_background/movie_01.jpg',
          '/images/bug_background/movie_02.jpg',
          '/images/bug_background/music.jpg',
          '/images/bug_background/nature.jpg',
          '/images/bug_background/nature_01.jpg',
          '/images/bug_background/news.jpg',
          '/images/bug_background/news_01.jpg',
          '/images/bug_background/news_02.jpg',
        ],
        splashScreenFile: null,
        timezones: [],
        isSaving: false,
        rules: {
          nameRules: [
            (v) => !!v || 'Name is required',
            (v) => (v && v.length <= 255) || 'Name must be less than 255 characters',
          ],
          adInsertionInterval: [
            (v) => (v > 0) || 'Invalid value',
          ],
          proportion: [
            (v) => !!v || 'Required',
            (v) => (v >= 0 && v <= 100) || 'Invalid value',
          ],
        },
        layoutType: LAYOUT_TYPE,
        openPlayer: false,
        player: {
          assetName: '',
          assetDescription: '',
        },
        frameSrc: '',
        //
        autoMode: false,
        showNote: false,
        tags: [],
        selectedTags: [],
        max: 8760,
        min: 0,
        scheduleRules: [
          {
            range: [0, 4],
            priority: '',
            priorities: [],
            valid: false,
            distance: 1,
          },
        ],
        priorities: [
          '10 (High)',
          '9 (High)',
          '8 (High)',
          '7 (Medium)',
          '6 (Medium)',
          '5 (Medium)',
          '4 (Medium)',
          '3 (Low)',
          '2 (Low)',
          '1 (Low)',
        ],
        priorityMap: {},
        fixedVODDialog: false,
        fixedVOD: {
          type: 'asset',
          assetId: 0,
          day: 'Monday',
          time: '10:00',
          slot: {
            name: '',
            duration: 60,
          },
        },
        fixedVODAssets: [
          'Movie1',
          'Movie2',
          'Movie3',
          'Movie4',
          'Movie5',
          'Commercial1',
          'Commercial2',
          'Commercial3',
          'Episode1',
          'Episode2',
          'Episode3',
        ],
        fixedVODDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      }
    },
    computed: {
      defaultIcon () {
        return '/images/ch_icon.png'
      },
      loading () {
        return this.$store.getters.isDataLoaded
      },
      isFormValid () {
        if (this.channel.name.length < 1 || this.channel.name.length > 255) {
          return false
        }
        if (this.channel.watermark.enabled && !this.channel.watermarkPictureId) {
          return false
        }
        return true
      },
      isScheduleSettingsValid () {
        if (!this.autoMode && this.channel.autoScheduler !== this.autoMode) {
          return true
        }
/*        if (this.selectedTags.length === 0) {
          return false
        }
        const indexes = {}
        for (let i = 0; i < this.selectedTags.length; i += 1) {
          indexes[this.selectedTags[i]] = true
        }
        for (let i = 0; i < this.tags.length; i += 1) {
          if (this.tags[i].proportion < 0 || this.tags[i].proportion > 100) {
            return false
          }
        }
        for (let i = 0; i < this.scheduleRules.length; i += 1) {
          if (!this.scheduleRules[i].valid) {
            return false
          }
        }*/
        return true
      },
    },
    created () {
      const channelId = this.$route.params.id
      this.parseQuery()
      this.loadData(channelId)
      for (let i = 0; i < this.priorities.length; i += 1) {
        this.priorityMap[this.priorities[i]] = i + 1
      }
    },
    methods: {
      validateRule (rule) {
        if ((rule.range[0] < rule.range[1]) && rule.priority !== '' && rule.distance > 0) {
          // eslint-disable-next-line no-param-reassign
          rule.valid = true
          return
        }
        // eslint-disable-next-line no-param-reassign
        rule.valid = false
      },
      changeRule (i, rule) {
        if (i === 0) {
          // eslint-disable-next-line no-param-reassign
          rule.range = [0, rule.range[1]]
        } else {
          // eslint-disable-next-line no-param-reassign
          rule.range = [this.scheduleRules[i - 1].range[1] + 1, rule.range[1]]
        }
        if (rule.range[1] <= rule.range[0]) {
          // eslint-disable-next-line no-param-reassign
          rule.range = [rule.range[0], rule.range[0] + 1]
        }
        this.validateRule(rule)
        let prev = rule
        for (let j = i + 1; j < this.scheduleRules.length; j += 1) {
          const r = this.scheduleRules[j]
          if (r.range[0] !== prev.range[1]) {
            const end = r.range[1] > prev.range[1] + 1 ? r.range[1] : prev.range[1] + 4
            r.range = [prev.range[1] + 1, end]
          }
          if (r.range[0] > this.max) r.range[0] = this.max
          if (r.range[1] > this.max) r.range[1] = this.max
          this.validateRule(r)
          prev = r
        }
      },
      changePriority () {
        let prev = this.scheduleRules[0]
        this.validateRule(prev)
        for (let j = 1; j < this.scheduleRules.length; j += 1) {
          const r = this.scheduleRules[j]
          r.priorities = []
          for (let k = 0; k < prev.priorities.length; k += 1) {
            if (this.priorities[k] === prev.priority) {
              for (let l = k; l < this.priorities.length; l += 1) {
                r.priorities.push(this.priorities[l])
              }
              break
            }
          }
          if (r.priorities.length === 0) {
            for (let k = 0; k < prev.priorities.length; k += 1) {
              if (prev.priorities[k] === prev.priority) {
                for (let m = k; m < prev.priorities.length; m += 1) {
                  r.priorities.push(prev.priorities[m])
                }
                break
              }
            }
          }
          let clearPriority = true
          for (let k = 0; k < r.priorities.length; k += 1) {
            if (r.priorities[k] === r.priority) {
              clearPriority = false
              break
            }
          }
          if (clearPriority) {
            r.priority = ''
          }
          this.validateRule(r)
          prev = r
        }
      },
      addRule () {
        const prev = this.scheduleRules[this.scheduleRules.length - 1]
        const newRule = {
          range: [prev.range[1] + 1, prev.range[1] + 4],
          priority: '',
          priorities: [],
          valid: false,
          distance: 1,
        }
        if (newRule.range[0] > this.max) newRule.range[0] = this.max
        if (newRule.range[1] > this.max) newRule.range[1] = this.max
        this.scheduleRules.push(newRule)
        this.changePriority()
      },
      deleteRule (i) {
        this.scheduleRules.splice(i, 1)
      },
      parseQuery () {
        const { tab } = this.$route.query
        if (tab) {
          this.tab = Number(tab) || 0
        }
      },
      changeRoute () {
        const newQuery = {
          tab: `${this.tab}`,
        }
        if (JSON.stringify(this.$route.query) === JSON.stringify(newQuery)) return
        this.$router.push({
          query: newQuery,
        }).catch((err) => {
          // eslint-disable-next-line no-console
          console.log(err)
        })
      },
      changeMode () {
        this.showNote = true
      },
      loadData (channelId) {
        this.$store.commit('setPageLoader')

        const tzReq = this.$store.dispatch('timezones')
        const args = {
          ids: channelId,
          expand: 'layout,timezone,templates,logo,splash_screen,watermark,auto_scheduler_rules,auto_scheduler_tags,xmltv',
        }
        const channelReq = this.$store.dispatch('channels', args)
        const tagArgs = {
          'channel.id[eq]': channelId,
          'auto[eq]': 1,
        }
        const tagReq = this.$store.dispatch('tags', tagArgs)

        return Promise.all([tzReq, channelReq, tagReq])
          .then((res) => {
            const timezones = res[0]
            this.timezones = timezones.map((tz) => processTimezone(tz))

            const channels = res[1]
            this.channel = processChannel(channels[0])
            this.watermarkSaved = JSON.parse(JSON.stringify(this.channel.watermark))

            this.autoMode = this.channel.autoScheduler

            if (this.channel.autoSchedulerRules.length) {
              this.scheduleRules = JSON.parse(JSON.stringify(this.channel.autoSchedulerRules))
              for (let i = 0; i < this.scheduleRules.length; i += 1) {
                if (this.scheduleRules[i].range[1] > this.max) {
                  this.max = this.scheduleRules[i].range[1]
                }
                if (this.scheduleRules[i].level > 0 &&
                  this.scheduleRules[i].level <= this.priorities.length) {
                  this.scheduleRules[i].priority = this.priorities[10 - this.scheduleRules[i].level]
                }
              }
            }
            this.scheduleRules[0].priorities = JSON.parse(JSON.stringify(this.priorities))
            if (!this.scheduleRules[0].priority) {
              // eslint-disable-next-line prefer-destructuring
              this.scheduleRules[0].priority = this.scheduleRules[0].priorities[0]
            }
            for (let i = 0; i < this.scheduleRules.length; i += 1) {
              this.validateRule(this.scheduleRules[i])
            }

            this.changePriority()

            const selectedTagMap = {}
            for (let i = 0; i < this.channel.autoSchedulerTags.length; i += 1) {
              const t = this.channel.autoSchedulerTags[i]
              selectedTagMap[t.tag.id] = t.proportion
            }

            const tags = res[2]
            const tagIndex = {}
            for (let i = 0; i < tags.length; i += 1) {
              const t = tags[i]
              tagIndex[t.id] = i
              const processed = processTag(t)
              // eslint-disable-next-line no-prototype-builtins
              if (selectedTagMap.hasOwnProperty(t.id)) {
                this.selectedTags.push(i)
                processed.proportion = selectedTagMap[t.id]
              } else {
                processed.proportion = 0
              }
              this.tags.push(processed)
            }
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
          .finally(() => this.$store.commit('resolvePageLoader'))
      },
      uploadImage (tag, fileObj) {
        const output = document.getElementById(tag)
        output.src = URL.createObjectURL(fileObj)
        // eslint-disable-next-line func-names
        output.onload = function () {
          URL.revokeObjectURL(output.src)
        }

        const file = new FormData()
        file.append('iconUploadFile', fileObj, fileObj.name)
        file.append('action', 'pre-upload')

        this.$store.dispatch('uploadChannelIcon', file)
          .then((result) => {
            this.channel[tag] = result.image_url || ''
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
      },
      fileUpload (tag, fileObj) {
        if (!fileObj) return
        const file = new FormData()
        file.append('uploadfile', fileObj, fileObj.name)

        this.$store.dispatch('upload', file)
          .then((result) => {
            Vue.set(this.channel, tag, result.blob)
            Vue.set(this.channel, `${tag}Id`, result.id)
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
      },
      playStreamByURL (stream, name, description) {
        const link = stream.replace(/^http:\/\//, 'https://')

        this.player.assetName = name
        this.player.assetDescription = description
        if (stream.endsWith('mp4')) {
          this.frameSrc = `/js/radiant/index4.html?src=${link}`
        } else {
          this.frameSrc = `/js/radiant/index2.html?src=${link}`
        }
        this.openPlayer = true
      },
      closeStream () {
        this.openPlayer = false
        this.frameSrc = '/js/radiant/index2.html?src='
      },
      goToNewImport () {
        this.$router.push({
          name: 'AutoSourceNewPage',
        })
      },
      changeProportion () {
      },
      changeSelectedTags () {
        const indexes = {}
        for (let i = 0; i < this.selectedTags.length; i += 1) {
          indexes[this.selectedTags[i]] = true
        }
        for (let i = 0; i < this.tags.length; i += 1) {
          // eslint-disable-next-line no-prototype-builtins
          if (indexes.hasOwnProperty(i)) {
            this.tags[i].proportion = Math.round(100 / this.selectedTags.length)
          } else {
            this.tags[i].proportion = 0
          }
        }
      },
      save () {
        this.isSaving = true
        if (!this.isFormValid) return

        if (this.channel.watermark.enabled &&
          (this.watermarkSaved.position !== this.channel.watermark.position ||
            this.watermarkSaved.size !== this.channel.watermark.size ||
            this.watermarkSaved.pictureId !== this.channel.watermarkPictureId)) {
          this.channel.watermark.transcodingUpdateRequired = true
        }

        const channel = channelToJsonAPI(this.channel)

        this.$store.dispatch('updateChannel', channel)
          .then(() => {
            this.$store.commit('showSnackbar', {
              text: 'The channel has been successfully updated',
              timeout: 3000,
              color: 'success',
            })
            this.isSaving = false
          })
          .catch((errorMsg) => {
            this.isSaving = false
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
      },
      goToScheduler () {
        this.$router.push({
          name: 'SchedulerPage',
          params: {
            id: this.channel.id,
          },
        })
      },
      saveScheduleSettings () {
        this.isSaving = true
        if (!this.isScheduleSettingsValid) return

        this.$store.commit('setPageLoader')

        const channel = channelToJsonAPI(this.channel)
        channel.auto_scheduler = this.autoMode
        channel.auto_scheduler_tags = []
        channel.auto_scheduler_rules = []

        const indexes = {}
        for (let i = 0; i < this.selectedTags.length; i += 1) {
          indexes[this.selectedTags[i]] = true
        }
        for (let i = 0; i < this.tags.length; i += 1) {
          // eslint-disable-next-line no-prototype-builtins
          if (indexes.hasOwnProperty(i)) {
            channel.auto_scheduler_tags.push({
              tag: { id: this.tags[i].id },
              proportion: this.tags[i].proportion,
            })
          }
        }

        for (let i = 0; i < this.scheduleRules.length; i += 1) {
          const r = this.scheduleRules[i]
          channel.auto_scheduler_rules.push({
            interval_start: r.range[0],
            interval_end: r.range[1],
            weight: 11 - this.priorityMap[r.priority],
            min_repeat_interval: Number(r.distance),
          })
        }

        this.$store.dispatch('updateChannel', channel)
          .then(() => {
            this.$store.commit('showSnackbar', {
              text: 'The auto scheduler has been successfully updated',
              timeout: 3000,
              color: 'success',
            })
            this.goToScheduler()
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
          .finally(() => {
            this.isSaving = false
            this.$store.commit('resolvePageLoader')
          })
      },
      addFixedVOD () {
      },
    },
  }
</script>

<style>
  .checkboxes.v-input .v-label {
    margin-bottom: 0 !important;
  }
  .radios .v-label {
    margin: 0 !important;
    line-height: 0 !important;
  }
  .added .v-input__slot {
    padding-bottom: 0 !important;
    margin-bottom: 0 !important;
  }
  .watermark .v-snack__wrapper {
    min-width: 100px !important;
    width: 100px !important;
  }
  .v-window-item .v-carousel__item {
    height: 360px !important;
  }
</style>
